import React from "react";
import { motion } from "framer-motion";
import { Github, Linkedin, Globe } from "lucide-react";
import assessmentImg from "./images/certificate.jpeg";
import cert1 from "./images/cert1.png";
import cert2 from "./images/cert2.png";
const cert1Pdf = "/cert1.pdf";
const cert2Pdf = "/cert2.pdf";
const user = {
  name: "Paul Akporarhe",
  title:
    "B2B and B2C Sales • Growth Ops • Data Projects • Customer Success & Client Relations",
  location: "Abuja, Nigeria",
  email: "akporarhe@gmail.com",
  phone: "+2348106519628",
  github: "https://github.com/lashakp",
  linkedin: "https://www.linkedin.com/in/paul-akporarhe/",
  huggingface: "https://huggingface.co/lashakp",
  tagline: `I bridge sales, client success, business development, and technical delivery — combining real-world commercial experience with data science and software engineering to solve business problems. I build practical solutions ranging from web scraping and analytics dashboards to machine learning systems, automation tools, and production-ready backend APIs using Python, FastAPI, React, Node.js, and modern data technologies.`,
  about: `I operate at the intersection of business execution, client success, sales, and technology. With over 8 years of experience across sales, business development, customer support, client success, call center operations, and technical delivery, I bring a practical understanding of how businesses acquire customers, manage relationships, improve workflows, and deliver results.

    Alongside my commercial experience, I design and deploy data and software solutions that solve real operational problems. My technical work includes web scraping and data acquisition systems, analytics pipelines and dashboards, workflow automation, text-to-speech prototypes, machine learning applications, and production-ready backend APIs.

    My professional experience also includes U.S.-focused outbound sales and customer operations, where I have worked across healthcare, insurance, pharmacy, and real estate campaigns. I have handled high-volume customer interactions, lead qualification, objection handling, appointment setting, live transfers, CRM management, and relationship-focused communication using platforms such as Convoso, VICIdial, Zoho CRM, Spoke Phone, and Roam.

    My approach is pragmatic and results-oriented: understand the business problem, identify the most effective solution, build reliable systems or processes, and continuously improve the outcome. Whether the challenge involves customers, sales pipelines, data, automation, or software, I focus on creating solutions that improve efficiency, decision-making, customer experience, and scalability.

    I’m open to full-time opportunities, freelance projects, technical builds, and partnerships where business outcomes meet technology. Let's build something that creates measurable impact and scales.`,

  // SKILLS (SPLIT CLEANLY)
  // =========================
technicalSkills: [
    "CRM Platforms & Customer Management Systems",
    "Convoso & VICIdial Dialer Systems",
    "Zoho CRM",
    "Spoke Phone & Roam",
    "CRM Documentation, Pipeline & Activity Tracking",
    "Customer Support & Remote Call Center Systems",
    "Microsoft Office Suite (Excel, Word, PowerPoint, Outlook)",
    "Google Workspace & Remote Collaboration Tools",
    "Advanced Excel (Dashboards, PivotTables, PivotCharts, KPIs, Power Query, VBA)",
    "Data Analysis, Reporting & Business Intelligence",
    "Sales, Customer & Operational Analytics",
    "Python",
    "SQL & SQLite",
    "Pandas & Data Processing",
    "Web Scraping (BeautifulSoup, Selenium)",
    "Machine Learning (Scikit-learn, Hugging Face)",
    "Text-to-Speech (Streamlit, ElevenLabs APIs)",
    "FastAPI & REST API Development",
    "API Design, Versioning, Pagination & Filtering",
    "Swagger / OpenAPI Documentation",
    "Docker & Containerization",
    "Backend Deployment (Render)",
    "Git & GitHub",
    "Machine Learning Integration & Safe Fallbacks",
    "Data Cleaning & Validation",
    "Workflow Automation",
    "Testable & Modular Service Architecture"
  ],

businessSkills: [
    "Customer Success & Client Relationship Management",
    "Customer Service, Retention & Experience",
    "B2B & B2C Sales",
    "Business Development & Outbound Prospecting",
    "Lead Generation & Qualification",
    "Appointment Setting & Meeting Coordination",
    "Consultative & Value-Based Selling",
    "Cold Calling & High-Volume Outreach",
    "Objection Handling & Difficult Conversations",
    "CRM & Sales Pipeline Management",
    "CRM Documentation & Workflow Management",
    "Account Management & Relationship Development",
    "Customer Onboarding & User Support",
    "Complaint Resolution & Escalation Management",
    "Active Listening & Needs Discovery",
    "Follow-Up & Lead Nurturing",
    "Customer Retention & Reactivation",
    "Sales Performance & KPI Tracking",
    "Sales Reporting & Pipeline Analysis",
    "Process Improvement & Workflow Optimization",
    "Stakeholder Communication & Coordination",
    "Cross-Functional Collaboration",
    "Problem Solving & Operational Support",
    "Time Management & Multitasking",
    "Remote Team Collaboration"
  ],
  
  // ==========================================================
  // PROJECT → SKILLS MAPPING (NEW SECTION)
  // ==========================================================
  projectSkillsMap: [
    {
      project: "U.S. Customer Success & Call Center Operations",
      skills: [
        "Customer Support",
        "Customer Success",
        "High-Volume Outbound Calling",
        "Lead Qualification",
        "Objection Handling",
        "Live Transfer Qualification",
        "CRM Documentation",
        "Convoso",
        "VICIdial",
        "Zoho CRM",
        "Customer Retention",
        "KPI & Performance Tracking",
      ],
    },
    {
      project: "B2B Real Estate Acquisition & Appointment Setting",
      skills: [
        "B2B Prospecting",
        "Cold Calling",
        "Lead Qualification",
        "Consultative Selling",
        "Objection Handling",
        "Appointment Setting",
        "Pipeline Management",
        "Zoho CRM",
        "Spoke Phone",
        "Roam",
        "Follow-Up & Lead Nurturing",
        "Sales KPI Tracking",
      ],
    },
    {
      project: "Developer Resource Intelligence API",
      skills: [
        "FastAPI (REST API Development)",
        "API Design & Versioning",
        "Docker & Containerization",
        "Backend Deployment on Render",
        "SQLite / Data Modeling",
        "Machine Learning Integration",
        "Explainable Recommendation Systems",
        "Swagger / OpenAPI Documentation",
        "Pagination & Filtering",
        "Testable & Modular Service Architecture"
      ],
    },
    {
      project: "Excel Sales & Inventory Dashboard",
      skills: [
        "Advanced Excel",
        "KPI Design",
        "PivotTables",
        "Inventory Analytics",
        "Forecasting",
        "VBA Automation",
        "Business Reporting",
      ],
    },
    {
      project: "Excel VBA Employee Management System",
      skills: [
        "Excel VBA",
        "Form Design",
        "CRUD Operations",
        "Data Validation",
        "Secure Data Management",
        "Automation",
      ],
    },
    {
      project: "Car Insurance Claim Prediction",
      skills: [
        "Machine Learning",
        "EDA",
        "Feature Engineering",
        "Model Evaluation",
        "Gradio Deployment",
      ],
    },
    {
      project: "Car Insurance Claims Data Analysis & Interactive Dashboard",
      skills: [
        "HTML",
        "Python",
        "EDA",
        "Dash",
        "Plotly",
        "Matplotlib",
        "Data Visualization",
        "Pandas",
        "Logging",
      ],
    },
    {
      project: "Africa Companies Scraper",
      skills: [
        "Python",
        "BeautifulSoup",
        "Pandas",
        "Web Scraping",
        "Data Extraction",
      ],
    },
  ],
  experience: [
    {
      company: "Fusion Growth Partners — Remote",
      role: "Acquisition Associate",
      dates: "Mar 2026 – Jul 2026",
      bullets: [
        "Conducted high-volume outbound prospecting targeting residential real estate professionals across the United States.",
        "Engaged business owners and real estate professionals through consultative conversations focused on business growth, investment partnerships, and long-term relationship development.",
        "Qualified prospects by identifying business needs, assessing fit, handling objections, and determining appropriate opportunities for further engagement.",
        "Scheduled qualified appointments for senior Partnership Executives and maintained consistent follow-up throughout the prospecting cycle.",
        "Managed lead pipelines, activity records, prospect information, and follow-up schedules using Zoho CRM.",
        "Used Spoke Phone, Roam, CRM platforms, and Microsoft Office tools to manage daily outreach, communication, documentation, and pipeline activities.",
        "Maintained accurate CRM records and ensured timely updates across prospect interactions, appointments, and follow-up activities.",
      ],
    },

    {
      company: "The Call Gurus — Remote",
      role: "Customer Success Representative / Call Center Agent",
      dates: "Feb 2025 – Jan 2026",
      bullets: [
        "Managed 400–500 outbound calls daily across multiple U.S.-based healthcare, insurance, pharmacy, and lead-generation campaigns in high-volume, KPI-driven environments.",
        "Supported campaigns including ExactCare Pharmacy, RX Pharmacy, The Health Scout, Magnitude ACA Marketplace, Final Expense (FE), Social Security Disability Insurance (SSDI), and Continuous Glucose Monitor (CGM) downselling.",
        "Qualified prospects through structured discovery and eligibility conversations, identified customer needs, handled objections, and delivered qualified live transfers to appropriate teams.",
        "Assisted customers with prescription delivery services, ACA Marketplace eligibility, pharmacy enrollment, insurance qualification, disability benefit screening, medication management, and related customer-service inquiries.",
        "Conducted Final Expense and SSDI qualification conversations, applying campaign-specific eligibility requirements while maintaining professional and empathetic communication.",
        "Maintained accurate customer records, call dispositions, notes, follow-ups, and lead statuses using Convoso, VICIdial, Zoho CRM, and client CRM systems.",
        "Used predictive dialers and CRM platforms to manage high-volume workflows while consistently working toward productivity, quality, transfer, and campaign performance targets.",
        "Applied active listening, rapport-building, objection handling, and adaptive communication techniques to improve customer engagement and transfer opportunities.",
        "Maintained compliance with campaign scripts, quality-assurance requirements, customer privacy expectations, and applicable campaign regulations.",
      ],
  },
  
    {
      company: "Uniccon Institute of Technology",
      role: "Intern Data Scientist, TTS & Web Scraping Engineer, digital solutions",
      dates: "Jan 2024 – Jan 2025",
      bullets: [
        "Designed and implemented digital solutions, integrating advanced technologies to enhance business operations and market intelligence.",
        "Text-to-Speech (TTS) Implementation: Managed projects from concept to deployment, including selecting appropriate TTS APIs, defining text input parameters, choosing optimal voices, generating high -quality audio outputs, and integrating TTS into customer engagement platforms for automated communication.",
        "Web Scraping & Data Acquisition: Oversaw web scraping initiatives to systematically collect and structure large datasets for market research, competitor analysis, and lead generation. This involved identifying target websites, designing scraping logic, extracting raw data, performing data cleaning and validation, and ensuring secure storage and accessibility.",
        "Data Analysis & Insight Generation: Utilized collected data to perform in-depth market analysis, identify emerging trends, and generate actionable business intelligence reports for strategic decision -making.",
        "Cross -functional Collaboration: Collaborated closely with technical development teams, external vendors, and business units to ensure seamless integration and optimal performance of digital tools.",
        "Contributed to the company's digital transformation roadmap by identifying new technological opportunities for efficiency and growth.",
      ],
    },

    {
      company: "Upskill Universe — Remote, Freelance",
      role: "Client Support Associate",
      dates: "Jul 2022 – Dec 2024",
      bullets: [
        "Assisted entrepreneurs and professionals with customer communication and operational support activities.",
        "Coordinated follow-ups, scheduling, and remote client communication across multiple engagements.",
        "Delivered tailored mentorship to entrepreneurs and small businesses.",
        "Implemented digital marketing strategies improving visibility by 60%.",
        "Trained clients on CRMs, analytics, and project tools.",
        "Ran sessions virtually using Google Workspace, Zoom, and Trello.",
      ],
    },

    {
      company: "Airtel Nigeria",
      role: "Territory Sales Manager",
      dates: " Feb 2019 – May 2022",
      bullets: [
        "Managed day-to-day sales and commercial operations across an assigned territory, with responsibility for revenue, acquisition, distribution, and performance targets.",
        "Managed relationships and accounts across distributors, retailers, and key business customers, ensuring consistent service delivery and commercial performance.",
        "Prospected and pitched Airtel business solutions to corporate and commercial clients, conducting needs analysis, qualifying opportunities, and closing strategic sales.",
        "Set and tracked sales KPIs and targets across assigned outlets and accounts, monitoring performance against monthly and periodic objectives.",
        "Conducted regular field visits to outlets to monitor sales performance, product availability, merchandising standards, and stock levels.",
        "Monitored inventory and coordinated replenishment with distributors and retail partners to minimize stock-outs and maintain product availability.",
        "Planned and executed sales activations, promotional campaigns, and market initiatives to drive customer acquisition, product visibility, and revenue growth.",
        "Analysed territory performance, market trends, competitor activity, outlet productivity, and sales data to identify opportunities for growth and improve execution.",
        "Managed customer and account issues, resolving service concerns and maintaining strong relationships to support retention and long-term commercial value.",
        "Coordinated with distributors, retailers, internal sales teams, and other cross-functional stakeholders to ensure effective territory execution and achievement of business objectives.",
        "Prepared detailed territory, sales, market, and performance reports to support management decision-making and track progress against targets.",
        "Managed trade receivables and supported credit-control activities, following up on outstanding balances and maintaining healthy distributor and account relationships.",
        "Supported the implementation of company sales strategies at territory level, translating broader commercial objectives into actionable field activities.",
        "Maintained accurate records of sales activities, account performance, outlet coverage, inventory status, and market intelligence.",

      ],
    },

    {
      company: "Guinness Nigeria Plc",
      role: "Sales Representative",
      dates: "Jan 2016 – Nov 2018",
      bullets: [
        "Managed daily customer and distributor interactions to support product distribution and sales performance.",
        "Maintained strong relationships with retail outlets and distribution partners.",
        "Tracked sales activity and ensured consistent product visibility within assigned territory.",
        "Delivered reliable service while managing multiple customer engagements.",
      ],
    }
  ],
  projects: [
    {
      title: "B2B Acquisition & Appointment Setting — Fusion Growth Partners",
      description:
        "Conducted outbound B2B prospecting targeting residential real estate professionals across the United States. Engaged business owners and real estate professionals through consultative conversations focused on business growth, investment partnerships, and long-term relationship development. Qualified prospects by identifying business needs, assessing fit, handling objections, and determining appropriate opportunities for further engagement. Scheduled qualified appointments for senior Partnership Executives while managing follow-ups and maintaining accurate prospect and pipeline records in Zoho CRM. Used Spoke Phone, Roam, CRM platforms, and Microsoft Office tools to manage daily outreach, communication, documentation, and sales activity within a KPI-driven business development environment.",
      tags: [
        "B2B Sales",
        "Business Development",
        "Outbound Prospecting",
        "Lead Generation",
        "Appointment Setting",
        "Lead Qualification",
        "Consultative Selling",
        "Objection Handling",
        "Pipeline Management",
        "Zoho CRM",
        "Spoke Phone",
        "Roam",
      ],
      link: null,
    },
    {
      title: "U.S. Customer Success & Call Center Operations — The Call Gurus",
      description:
        "Managed high-volume customer interactions and lead qualification across multiple U.S.-based healthcare, insurance, pharmacy, and customer acquisition campaigns. Worked across campaigns including ExactCare Pharmacy, RX Pharmacy, The Health Scout, Magnitude ACA Marketplace, Final Expense (FE), Social Security Disability Insurance (SSDI), and Continuous Glucose Monitor (CGM) downselling. Handled approximately 400–500 outbound calls daily, qualifying prospects, identifying customer needs, handling objections, maintaining professional customer interactions, and delivering qualified live transfers. Maintained accurate CRM records, dispositions, notes, and follow-ups while working with Convoso, VICIdial, Zoho CRM, and campaign-specific systems in KPI- and quality-driven environments.",
      tags: [
        "Customer Success",
        "Customer Support",
        "Call Center Operations",
        "Outbound Calling",
        "Lead Qualification",
        "Objection Handling",
        "Live Transfers",
        "CRM",
        "Convoso",
        "VICIdial",
        "Zoho CRM",
        "KPI Performance",
      ],
      link: null,
    },

    {
      title: "Developer Resource Intelligence API",
      description: "Developed a production-ready, explainable backend API to recommend curated developer learning resources. Work included designing a deterministic ranking system, integrating an optional ML-based ranking model with safe fallback, building RESTful endpoints with FastAPI, creating a Dockerized deployment for reproducibility, implementing pagination and filtering, and providing an interactive Swagger UI for live API exploration. Delivered a fully versioned, testable, and portable service, accessible in demo mode without credentials or full mode with authenticated API key access. Note: This service is deployed on Render's free-tier infrastructure. The API may take a few seconds to wake up after periods of inactivity.",
      tags: [
        "Python",
        "FastAPI",
        "REST API",
        "Docker",
        "Machine Learning",
        "API Design",
        "Backend",
        "Explainable AI"
      ],
      link: "https://developer-resource-intelligence-api.onrender.com/docs"
    },
    {
      title: "Car Insurance Claim Prediction (Machine Learning)",
      description:
        "Developed a full end-to-end machine learning pipeline to predict whether a customer would file an insurance claim. Work included data cleaning, preprocessing, outlier handling, feature engineering (including a custom risk scoring metric), EDA visualizations, train/test splits, scaling, one-hot encoding, and model training using logistic regression, random forest, and CatBoost. Delivered an interactive Gradio web app allowing users to input driver details and instantly receive claim predictions with model explanations.",
      tags: [
        "Python",
        "Machine Learning",
        "EDA",
        "Feature Engineering",
        "CatBoost",
        "Data Visualization",
        "Gradio",
      ],
      link: "https://huggingface.co/spaces/lashakp/car-insurance-claim-prediction",
    },
     {
      title: "Car Insurance Claims Data Analysis & Interactive Dashboard",
      description:
        "Designed and implemented a full analytics pipeline to explore, clean, process, and visualize 10,000+ car insurance customer records. Work included resolving missing data, validating numeric and binary fields, transforming categorical values (e.g., vehicle ownership and claim status), and generating automated summary logging. Conducted exploratory data analysis with both static (Matplotlib/Seaborn) and interactive (Plotly) visualizations, covering claim patterns by age, experience, ownership, violations, mileage, and more. Delivered a production-ready Dash web dashboard that presents key insights, metrics, and trends, with all outputs programmatically saved—including logs, reports, metrics, and visual assets—in a structured project directory.",
      tags: [
        "Python",
        "Data Cleaning",
        "EDA",
        "Dash",
        "Plotly",
        "Matplotlib",
        "Data Visualization",
        "Pandas",
        "Logging",
      ],
      link: "https://huggingface.co/spaces/lashakp/insurance-claim-dashboard",
    },
    {
      title: "Excel Sales & Inventory Dashboard",
      description:
        "A professional end-to-end Excel analytics solution simulating real-world sales and inventory operations. Features include data cleaning, PivotTable aggregation, KPI design, interactive dashboards with slicers, low-stock monitoring, sales forecasting with confidence bands, and VBA automation for refreshes and alerts. Designed to be portfolio-ready for GitHub, LinkedIn, and technical interviews.",
      tags: ["Excel", "Dashboard", "VBA", "Data Analysis", "PivotTables", "KPI", "Forecasting", "Automation", "Inventory Management"],
      link: "https://github.com/lashakp/Excel-Sales-Inventory-Dashboard-Project",
    },
    {
      title: "Excel VBA Employee Management System",
      description:
        "A fully automated Excel-based Employee Data Entry Form integrated with a secure database. Features interactive data entry, dropdown validation for Gender and Department, automated CRUD operations (Save, Modify, Delete, Reset), username and timestamp tracking, protected sheets, and robust error handling to ensure accurate and secure employee data management.",
      tags: ["Excel", "VBA", "Data Entry", "Forms", "CRUD", "Automation", "Validation"],
      link: "https://github.com/lashakp/Excel-VBA-Employee-Form",
    },

    {
      title: "Employee Data Cleaning and Transformation (SQL)",
      description:
        "Built a SQL pipeline to clean and standardize messy HR employee records, including trimming names, validating emails, normalizing phone numbers, converting messy salary and hire date formats, and flagging/archiving invalid data. Delivered a cleaned dataset ready for reporting and analytics.",
      tags: ["SQL", "Data Cleaning", "ETL", "Database"],
      link: "https://github.com/lashakp/employees_cleaning_SQL/blob/main/clean_data.sql",
    },
    {
      title: "Pidgin TTS Demo App",
      description:
        "Streamlit prototype that converts conversational text into natural-sounding speech using TTS APIs — tested with local dialects.",
      tags: ["Streamlit", "TTS", "APIs"],
      link: "https://github.com/lashakp/elevenlabs-tts-nigerian-pidgin",
    },
    {
      title: "Africa Companies Scraper",
      description:
        "A Python-based web scraping project that extracts structured data from the Wikipedia page listing the largest companies in Africa by revenue. The scraper parses HTML tables using BeautifulSoup, cleans and organizes the data with Pandas, and exports it as a CSV file for further analysis or downstream use.",
      tags: ["Python", "BeautifulSoup", "Pandas", "Web Scraping", "Data Extraction"],
      link: "https://github.com/lashakp/top-100-companies-africa/blob/main/africa_companies.csv",
    },

    {
      title: "Breast Cancer Prediction Model",
      description:
        "An ML model to predict breast cancer recurrence. Includes EDA, feature ranking, and an interactive Gradio app deployed on Hugging Face Spaces.",
      tags: ["Machine Learning", "Gradio", "Hugging Face"],
      link: "https://huggingface.co/spaces/lashakp/breast-cancer-recurrence-prediction",
    },
  ],
};

// Small presentational components
function IconLink({ href, children, Icon }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-2 text-slate-700 hover:text-blue-700"
    >
      {Icon && <Icon size={18} />} {children}
    </a>
  );
}
function Section({ title, children, id }) {
  return (
    <section id={id} className="bg-white p-6 rounded-2xl shadow-sm">
      {title && <h3 className="text-xl font-semibold mb-4">{title}</h3>}
      {children}
    </section>
  );
}
function ProjectCard({ p }) {
  const hasLink = Boolean(p.link);
  const isHash = hasLink && p.link.startsWith("#");
  return (
    <article className="bg-white p-4 rounded-xl shadow-sm">
      <h4 className="font-semibold">{p.title}</h4>
      <p className="mt-2 text-sm text-slate-600">{p.description}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        {p.tags.map((t) => (
          <span key={t} className="text-xs px-2 py-1 bg-slate-100 rounded">
            {t}
          </span>
        ))}
      </div>
      <div className="mt-4">
        {!hasLink || isHash ? (
          <button
            onClick={(e) => e.preventDefault()}
            className="text-sm text-blue-600 hover:underline"
            aria-disabled="true"
            title={hasLink ? "No external link" : "Link unavailable"}
          >
            {hasLink ? "Learn more" : "No external link"}
          </button>
        ) : (
          <a
            href={p.link}
            target={p.link && p.link.startsWith("http") ? "_blank" : undefined}
            rel={p.link && p.link.startsWith("http") ? "noopener noreferrer" : undefined}
            className="text-sm text-blue-600 hover:underline"
          >
            Learn more
          </a>
        )}
      </div>
    </article>
  );
}
function ExperienceItem({ exp }) {
  return (
    <div className="border-b pb-4">
      <h4 className="text-lg font-bold">{exp.role}</h4>
      <p className="text-sm text-slate-600">
        {exp.company} • {exp.dates}
      </p>
      <ul className="list-disc ml-5 mt-2 text-sm text-slate-700">
        {exp.bullets.map((b, i) => (
          <li key={i}>{b}</li>
        ))}
      </ul>
    </div>
  );
}
export default function App() {
  return (
    <div className="bg-gray-50 min-h-screen">
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 grid gap-12">
        {/* Hero / About */}
        <header className="flex flex-col sm:flex-row justify-between items-center sm:items-start mt-6">
          <div className="text-center flex-1">
            <img
              src="/profile.jpg"
              alt="Profile"
              className="w-32 h-32 rounded-full mx-auto mb-4 shadow-md object-cover"
            />
            <h1 className="text-3xl sm:text-4xl font-bold mb-2">{user.name}</h1>
            <p className="text-lg text-slate-700 mb-6">{user.title}</p>
            <motion.h2
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
              className="text-xl sm:text-2xl font-extrabold leading-tight"
            >
              {user.tagline}
            </motion.h2>
            <p className="mt-4 text-slate-600 max-w-2xl mx-auto">{user.about}</p>
          </div>
          <div className="flex flex-col gap-3 mt-6 sm:mt-0 sm:ml-6">
            <IconLink href={user.linkedin} Icon={Linkedin}>
              LinkedIn
            </IconLink>
            <IconLink href={user.github} Icon={Github}>
              GitHub
            </IconLink>
            <IconLink href={user.huggingface} Icon={Globe}>
              Hugging Face
            </IconLink>
          </div>
        </header>
        {/* Project Preview */}
        <section className="bg-gradient-to-br from-slate-100 to-white p-6 rounded-xl">
          <div className="rounded-lg border border-slate-200 overflow-hidden shadow-sm">
            <img
              src="/huggingface-preview.png"
              alt="car insurance claim Prediction App Preview"
              className="w-full object-cover"
            />
            <a
              href="https://huggingface.co/spaces/lashakp/car-insurance-claim-prediction"
              target="_blank"
              rel="noopener noreferrer"
              className="block text-center px-4 py-2 bg-blue-600 text-white font-medium hover:bg-blue-700"
            >
              Try Live Demo
            </a>
          </div>
          <div className="mt-3 text-center text-slate-600 text-sm">
            🔮 Try my interactive ML model predicting car insurance claim outcomes.
          </div>
        </section>
        {/* Skills */}
        <Section title="Skills">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Technical Skills */}
            <div>
              <h4 className="font-semibold mb-3">Technical Skills</h4>
              <div className="flex flex-wrap gap-2">
                {user.technicalSkills.map((skill) => (
                  <span
                    key={skill}
                    className="text-sm px-3 py-1 bg-slate-100 rounded-full"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>
            {/* Business / Customer Skills */}
            <div>
              <h4 className="font-semibold mb-3">Business & Customer Skills</h4>
              <div className="flex flex-wrap gap-2">
                {user.businessSkills.map((skill) => (
                  <span
                    key={skill}
                    className="text-sm px-3 py-1 bg-slate-100 rounded-full"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </Section>
        {/* Project Skills Mapping (NEW SECTION) */}
        <Section title="How My Skills Map to Projects">
          <div className="grid gap-4">
            {user.projectSkillsMap.map((item) => (
              <div key={item.project} className="bg-white p-4 rounded-xl shadow-sm">
                <h4 className="font-semibold mb-2">{item.project}</h4>
                <div className="flex flex-wrap gap-2">
                  {item.skills.map((s) => (
                    <span
                      key={s}
                      className="text-xs px-2 py-1 bg-slate-100 rounded"
                    >
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Section>
        {/* Experience */}
        <Section id="experience" title="Experience">
          <div className="grid gap-6">
            {user.experience.map((exp) => (
              <ExperienceItem key={exp.company} exp={exp} />
            ))}
          </div>
        </Section>
        {/* Selected Projects (moved up so it doesn't render last) */}
        <section id="projects" className="grid gap-6">
          <h3 className="text-xl font-semibold">Selected Projects</h3>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
            {user.projects.map((p) => (
              <ProjectCard key={p.title} p={p} />
            ))}
          </div>
        </section>
        {/* Certifications */}
        <section className="p-6 bg-gray-50 rounded-xl shadow-sm">
          <h2 className="text-2xl font-bold mb-4">Certifications</h2>
          <div className="grid md:grid-cols-2 gap-6 items-start">
            <div>
              <p className="mb-4 text-slate-700">
                Successfully completed a professional certification in{" "}
                <strong>National Skills Qualification Certificate in Data Science with Python Level 5 from</strong>{" "}
                <strong>[Computer Professionals Registration Council of Nigeria]</strong>, demonstrating verified
                skills in <strong>Data Analysis, Machine Learning & Visualization with Python</strong>.
              </p>
            </div>
            <div>
              <div className="mb-6">
                <a href="/CPN_NSQ_Certificate.pdf" target="_blank" rel="noopener noreferrer">
                  <img
                    src={assessmentImg}
                    alt="Professional Certificate Proof"
                    className="rounded-xl shadow-md w-full"
                  />
                </a>
                <a
                  href="/CPN_NSQ_Certificate.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block mt-2 text-blue-600 hover:underline text-sm"
                >
                  View Full Certificate (PDF)
                </a>
              </div>
              <div className="mb-6">
                <a href={cert1Pdf} target="_blank" rel="noopener noreferrer" title="Open cert1 PDF">
                  <img
                    src={cert1}
                    alt="Fundamentals of Digital Marketing"
                    className="rounded-xl shadow-md w-full h-64 object-cover"
                  />
                </a>
                <div className="mt-2 flex gap-3">
                  <a
                    href={cert1Pdf}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:underline"
                  >
                    View Fundamentals of Digital Marketing (PDF)
                  </a>
                  <a
                    href={cert1}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-slate-600 hover:underline"
                  >
                    View Image
                  </a>
                </div>
              </div>
              <div>
                <a href={cert2Pdf} target="_blank" rel="noopener noreferrer" title="Open cert2 PDF">
                  <img
                    src={cert2}
                    alt="Hustle Academy"
                    className="rounded-xl shadow-md w-full h-64 object-cover"
                  />
                </a>
                <div className="mt-2 flex gap-3">
                  <a
                    href={cert2Pdf}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:underline"
                  >
                    View Hustle Academy certificate (PDF)
                  </a>
                  <a
                    href={cert2}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-slate-600 hover:underline"
                  >
                    View Image
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* Letter of Recommendation */}
        <Section title="Letter of Recommendation">
          <div className="border border-slate-200 rounded-lg p-6 bg-slate-50">
            <div className="mb-4">
              <p className="text-sm text-slate-600 italic">From Omeife Technologies, Uniccon Group of Companies</p>
              <p className="text-sm text-slate-600 italic">Date: 1st August 2025</p>
            </div>
            <div className="mb-4">
              <a href="/Letter_of_Recommendation_Paul.pdf" target="_blank" rel="noopener noreferrer">
                <iframe
                  src="/Letter_of_Recommendation_Paul.pdf"
                  title="Letter of Recommendation Preview"
                  className="rounded-xl shadow-md w-full h-64"
                />
              </a>
            </div>
            <a
              href="/Letter_of_Recommendation_Paul.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
            >
              View Full Letter (PDF)
            </a>
          </div>
        </Section>
        {/* Resume */}
        <Section title="Resume">
          <div className="border border-slate-200 rounded-lg p-6 bg-slate-50">
            <p className="text-sm text-slate-600 mb-4">
              View my complete resume detailing my experience, skills, and projects.
            </p>
            <a
              href="/resume.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
            >
              View Resume (PDF)
            </a>
          </div>
        </Section>
        {/* Contact */}
        <Section id="contact" title="Get in touch">
          <p className="mt-2 text-sm text-slate-600">
            Prefer email?{" "}
            <a href={`mailto:${user.email}`} className="text-blue-600 hover:underline">
              {user.email}
            </a>
          </p>
          <p className="mt-2 text-sm text-slate-600">
            Phone:{" "}
            <a href={`tel:${user.phone}`} className="text-blue-600 hover:underline">
              {user.phone}
            </a>
          </p>
        </Section>
        {/* Footer */}
        <footer className="text-center py-8 text-slate-500">
          <div>
            Built by {user.name} —{" "}
            <a href={user.github} className="text-blue-600 hover:underline">
              View source
            </a>
          </div>
          <div className="text-xs mt-2">Made with React + Tailwind. Deployed on Vercel.</div>
        </footer>
      </main>
    </div>
  );
}
